import { createRouter, createWebHistory } from 'vue-router'
import type { LocationQuery, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import {
  DEFAULT_AW_MISSIONS_TEAM_SLUG,
  DEFAULT_TOOL_SELECTOR_PATH,
  canonicalizeAwMissionsPath,
  isAwMissionsLaunch,
  isAllowedInLaunch,
  readLaunchConfig,
  sanitizeLaunchReturnTo,
} from './config/launch'
import { hasUsableAccessToken, readSession } from './services/auth/session'
import { getFreshAccessToken } from './services/auth/tokenRefresh'

const DOCS_RECENT_KEY = 'aw-docs:recent-repos'
const DOCS_DEFAULT_REPO = 'GoHighLevel/ghl-aw-docs'
const CURRENT_TEAM_KEY = 'teamofone.currentTeamId'
const CURRENT_TEAM_SLUG_KEY = 'teamofone.currentTeamSlug'
const LEGACY_TEAM_KEY = 'hl.teamId'

export const APP_SECTIONS = ['radar', 'ship', 'team', 'docs', 'missions', 'metrics', 'design', 'knowledge'] as const
export type AppSection = (typeof APP_SECTIONS)[number]

function recentDocRepo(): string {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(DOCS_RECENT_KEY) : null
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
        return parsed[0]
      }
    }
  } catch {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(DOCS_RECENT_KEY)
    }
  }
  return DOCS_DEFAULT_REPO
}

function defaultWorkspacePath(): string {
  return DEFAULT_TOOL_SELECTOR_PATH
}

function storedTeamSlug(): string {
  if (typeof localStorage === 'undefined') return DEFAULT_AW_MISSIONS_TEAM_SLUG
  return (
    localStorage.getItem(CURRENT_TEAM_SLUG_KEY) ||
    localStorage.getItem(CURRENT_TEAM_KEY) ||
    localStorage.getItem(LEGACY_TEAM_KEY) ||
    DEFAULT_AW_MISSIONS_TEAM_SLUG
  )
}

function missionRedirectTarget(
  to: { query: LocationQuery; hash: string },
  tail = '',
): { path: string; query: LocationQuery; hash: string } {
  const queryTeam = typeof to.query.team === 'string' ? to.query.team : null
  const teamSlug = queryTeam || storedTeamSlug()
  const query = { ...to.query }
  delete query.team
  return {
    path: `/too/${encodeURIComponent(teamSlug)}/missions${tail}`,
    query,
    hash: to.hash,
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('./modules/login/pages/LoginPage.vue'),
  },
  {
    path: '/too/onboarding',
    redirect: DEFAULT_TOOL_SELECTOR_PATH,
  },
  {
    path: '/too/coming-soon',
    name: 'coming-soon',
    component: () => import('./modules/launch/pages/ComingSoonPage.vue'),
  },
  {
    path: DEFAULT_TOOL_SELECTOR_PATH,
    name: 'tool-selector',
    component: () => import('./modules/tools/pages/ToolSelectorPage.vue'),
  },

  // AI workspace shell. Sections drive AIModeOverlay active navigation.
  {
    path: '/app',
    name: 'app',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/app/routines',
    name: 'legacy-missions-section',
    redirect: (to) => ({ path: '/app/missions', query: to.query, hash: to.hash }),
  },
  {
    path: '/app/missions',
    name: 'app-missions',
    redirect: (to) => missionRedirectTarget(to),
  },
  {
    path: '/app/missions/new',
    name: 'app-mission-new',
    redirect: (to) => missionRedirectTarget(to, '/new'),
  },
  {
    path: '/app/missions/templates',
    name: 'app-mission-templates',
    redirect: (to) => missionRedirectTarget(to, '/templates'),
  },
  {
    path: '/app/missions/templates/:templateId/enable',
    name: 'app-mission-template-enable',
    redirect: (to) =>
      missionRedirectTarget(
        to,
        `/templates/${encodeURIComponent(String(to.params.templateId))}/enable`,
      ),
  },
  {
    path: '/app/missions/:routineId/edit',
    name: 'app-mission-edit',
    redirect: (to) =>
      missionRedirectTarget(
        to,
        `/${encodeURIComponent(String(to.params.routineId))}/edit`,
      ),
  },
  {
    path: '/app/:section(radar|ship|team|docs|missions|metrics|design|knowledge)',
    name: 'app-section',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },

  // AW Docs standalone launch routes.
  {
    path: '/too/collab',
    name: 'collab',
    redirect: () => `/too/docs/${recentDocRepo()}/README.md`,
  },
  {
    path: '/too/docs/:owner/:repo/:path(.*)*',
    name: 'doc-reader',
    component: () => import('./modules/docs/pages/DocReaderPage.vue'),
  },
  {
    path: '/too/:teamId/docs',
    name: 'team-docs',
    redirect: (to) => `/too/${to.params.teamId}/docs/${recentDocRepo()}/README.md`,
  },
  {
    path: '/too/:teamId/docs/:owner/:repo/:path(.*)*',
    name: 'team-doc-reader',
    component: () => import('./modules/docs/pages/DocReaderPage.vue'),
    props: true,
  },
  {
    path: '/too/prs/:owner/:repo/:number(\\d+)',
    name: 'pr-review',
    component: () => import('./modules/launch/pages/ComingSoonPage.vue'),
  },
  {
    path: '/too/docs/:id',
    name: 'doc-reader-mock',
    redirect: () => `/too/docs/${DOCS_DEFAULT_REPO}/README.md`,
  },

  // Legacy /too routes render the AI workspace shell directly so old deep
  // links keep their route names and the launch gate can report the original
  // URL instead of a post-redirect /app URL.
  { path: '/', redirect: DEFAULT_TOOL_SELECTOR_PATH },
  { path: '/too/', redirect: defaultWorkspacePath },
  {
    path: '/too/home',
    name: 'home',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/home-focus',
    name: 'home-focus',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/home-ops',
    name: 'home-ops',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/radar',
    name: 'radar-legacy',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/metrics',
    name: 'metrics-legacy',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/:teamSlug/radar',
    name: 'radar',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/team',
    name: 'team-scoped',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/missions',
    name: 'missions',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/missions/new',
    name: 'team-mission-new',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/missions/templates',
    name: 'team-mission-templates',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/missions/templates/:templateId/enable',
    name: 'team-mission-template-enable',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/missions/:routineId/edit',
    name: 'team-mission-edit',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/missions/:routineId/runs/:runId',
    name: 'team-mission-run',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/:teamSlug/metrics',
    name: 'metrics',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/projects',
    name: 'projects',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/:teamId/loop',
    name: 'loop',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
    props: true,
  },
  {
    path: '/too/loop',
    name: 'loop-legacy',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/gates',
    name: 'gates',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/team',
    name: 'team',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/teams',
    name: 'teams',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  {
    path: '/too/obs',
    name: 'obs',
    component: () => import('./modules/ai-mode/pages/AiWorkspaceShell.vue'),
  },
  { path: '/too/org', redirect: () => `/too/${encodeURIComponent(storedTeamSlug())}/team` },

  {
    path: '/:pathMatch(.*)*',
    redirect: defaultWorkspacePath,
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

const PUBLIC_PATHS = new Set(['/login'])

function isDevSignedIn(): boolean {
  return (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('teamofone.devSignedIn') === '1'
  )
}

function isLocalAuthHost(): boolean {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1'
}

function isLocalEmailSignedIn(): boolean {
  return isLocalAuthHost() && isDevSignedIn()
}

function isAuthentikCallback(to: RouteLocationNormalized): boolean {
  return (
    to.path === '/login' &&
    typeof to.query.code === 'string' &&
    typeof to.query.state === 'string'
  )
}

function safeFullModeReturnTo(raw: unknown): string {
  if (typeof raw !== 'string') return DEFAULT_TOOL_SELECTOR_PATH
  const candidate = raw.trim()
  if (
    !candidate ||
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    candidate.includes(':') ||
    candidate.startsWith('/login')
  ) {
    return DEFAULT_TOOL_SELECTOR_PATH
  }
  return candidate
}

function authenticatedLoginRedirect(to: RouteLocationNormalized): string {
  const launchConfig = readLaunchConfig()
  const rawReturnTo = to.query.returnTo
  const returnTo = typeof rawReturnTo === 'string' ? rawReturnTo : DEFAULT_TOOL_SELECTOR_PATH
  if (
    returnTo === '/' ||
    returnTo === '/app' ||
    returnTo === '/too/onboarding' ||
    returnTo === DEFAULT_TOOL_SELECTOR_PATH
  ) {
    return DEFAULT_TOOL_SELECTOR_PATH
  }
  if (launchConfig.releaseMode !== 'full') {
    const sanitized = sanitizeLaunchReturnTo(
      returnTo,
      launchConfig,
    )
    return isAwMissionsLaunch(launchConfig)
      ? canonicalizeAwMissionsPath(sanitized, storedTeamSlug()) ?? sanitized
      : sanitized
  }
  return safeFullModeReturnTo(rawReturnTo)
}

async function hasSsoAuth(): Promise<boolean> {
  if (readSession() === null) return false
  if (hasUsableAccessToken()) return true
  return (await getFreshAccessToken()) !== null
}

function resolveLaunchGate(fullPath: string) {
  const launchConfig = readLaunchConfig()
  const canonicalAwMissionsPath = isAwMissionsLaunch(launchConfig)
    ? canonicalizeAwMissionsPath(fullPath, storedTeamSlug())
    : null
  if (canonicalAwMissionsPath && canonicalAwMissionsPath !== fullPath) {
    return canonicalAwMissionsPath
  }
  if (isAllowedInLaunch(fullPath, launchConfig)) return true
  // Bare "/app" (and "/") is the SPA's home route. LoginPage and a handful of
  // other call sites still navigate there post-auth. Under AW_MISSIONS / AW_DOCS
  // launches that path is NOT whitelisted, which previously bounced users to
  // "/too/coming-soon?from=/app" on every fresh login. Treat those generic
  // "home" paths as a hint to land on the launch's default surface instead of
  // showing the unreleased-feature placeholder.
  const cleanPath = fullPath.split('?')[0]?.split('#')[0] ?? fullPath
  if (cleanPath === '/' || cleanPath === '/app') {
    const fallback = defaultWorkspacePath()
    if (fallback !== fullPath) return fallback
  }
  return { name: 'coming-soon', query: { from: fullPath } }
}

router.beforeEach(async (to) => {
  const mode =
    (import.meta.env.VITE_TEAMOFONE_AUTH_MODE as string | undefined) ?? 'sso'

  if (PUBLIC_PATHS.has(to.path)) {
    if (isAuthentikCallback(to)) return true
    const authed = mode === 'dev' ? isDevSignedIn() : isLocalEmailSignedIn() || await hasSsoAuth()
    if (authed) {
      return authenticatedLoginRedirect(to)
    }
    return true
  }

  if (mode === 'dev') {
    if (isDevSignedIn()) {
      return resolveLaunchGate(to.fullPath)
    }
    return { path: '/login', query: { returnTo: to.fullPath } }
  }

  if (isLocalEmailSignedIn() || await hasSsoAuth()) {
    return resolveLaunchGate(to.fullPath)
  }
  return { path: '/login', query: { returnTo: to.fullPath } }
})

export default router
